import React, {useEffect, useRef, useState, useCallback, useMemo} from 'react'
import styles from './WorkReportingPage.module.css'
import {
    AppBar,
    Button, Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText, FormControlLabel,
} from '@material-ui/core'
import IconButton from '@mui/material/IconButton';
import HookForm from '../../components/common/HookForm'
import SignatureCanvas from 'react-signature-canvas'
import TextField from '@mui/material/TextField'
import { v4 as uuid } from "uuid";
import Compressor from 'compressorjs';
import { jwtDecode } from "jwt-decode";

import {
    Alert,
    FormControl, FormHelperText, FormLabel,
    Grid,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    InputLabel, ListItemAvatar,
    MenuItem, Paper, Radio, RadioGroup,
    Select
} from '@mui/material'
import { useParams } from 'react-router'
import {
    DataListObject,
    FieldListObject, FieldListValues, SectionData,
    useUpdateWorkReporting, WorkReportingDataObject, WorkSections,
} from '../../queries/useDispatch'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import cn from 'classnames'
import {Status, Wrapper} from '@googlemaps/react-wrapper'

import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DeleteIcon from '@mui/icons-material/Delete';
import { Add } from '@mui/icons-material'
import Dropzone, {useDropzone} from "react-dropzone";
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import axios from "axios";
import PdfViewerComponent from "../../components/PdfViewerComponent";
import dayjs, {Dayjs} from "dayjs";
import LoadingBackdrop from "../../components/common/LoadingBackdrop";
import Snackbar from "@mui/material/Snackbar";
import {setNetworkState, useNetworkOnline} from "../../slices/authSlice";
import {getConfig} from "../../AppConfig";
import { db } from './db';
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {Controller, useForm} from "react-hook-form";
import NumberFormat from "react-number-format";
import {MobileDateTimePicker} from "@mui/x-date-pickers/MobileDateTimePicker";
import {useDispatch} from "react-redux";
import Autocomplete from "@mui/material/Autocomplete";


interface Params {
    userGuid: string
}
interface FormData {
    readonly: boolean
}

let previousValues = {}


// Move this ABOVE the WorkReportingPage component, outside it entirely
const MapComponent: React.FC<{
    center: { lat: number; lng: number }
    zoom: number
}> = ({ center, zoom }) => {
    const mapRef = useRef<HTMLDivElement>()
    const mapInstanceRef = useRef<any>(null)

    useEffect(() => {
        if (mapInstanceRef.current) return // already initialized, don't recreate

        // @ts-ignore
        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
            center,
            zoom
        })

        // @ts-ignore
        const marker = new window.google.maps.Marker({
            position: center,
            map: mapInstanceRef.current
        })

        marker.addListener("click", () => {
            window.open("https://maps.google.com?q=" + center.lat + "," + center.lng)
        })
    }, []) // empty deps - only run once on mount

    return (
        <div
            ref={mapRef}
            style={{ backgroundColor: 'lightblue', width: '100%', height: '300px' }}
        />
    )
}

const DynamicTextField = React.memo(({
                                         controlName,
                                         controlData,
                                         isReadOnly,
                                         error,
                                         field,
                                         onBlur,
                                     }: {
    controlName: string
    controlData: FieldListObject
    isReadOnly: boolean
    error: any
    field: any
    onBlur: (controlName: string, e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}) => {
    return (
        <TextField
            {...field}
            label={controlData.lbl}
            multiline
            rows={controlData.ctrlo}
            name={controlName}
            fullWidth
            defaultValue=""
            size="small"
            required={controlData.req}
            disabled={controlData.ro || isReadOnly}
            onBlur={(e) => onBlur(controlName, e)}
            error={!!error}
            helperText={error ? error.message : null}
            InputLabelProps={{ shrink: true }}
        />
    )
}, (prev, next) => {
    return (
        prev.controlName === next.controlName &&
        prev.isReadOnly === next.isReadOnly &&
        prev.error === next.error &&
        prev.field.value === next.field.value
    )
})

const DynamicNumberFormatField = React.memo(({
                                                 controlName,
                                                 controlData,
                                                 isReadOnly,
                                                 error,
                                                 field,
                                                 onBlur,
                                             }: {
    controlName: string
    controlData: FieldListObject
    isReadOnly: boolean
    error: any
    field: any
    onBlur: (controlName: string, e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}) => (
    <NumberFormat
        {...field}
        customInput={TextField}
        label={controlData.lbl}
        name={controlName}
        size={'small' as any}
        allowNegative={false}
        variant="outlined"
        fullWidth
        defaultValue=""
        required={controlData.req}
        disabled={controlData.ro || isReadOnly}
        onBlur={(e) => onBlur(controlName, e)}
        error={!!error}
        helperText={error ? error.message : null}
        InputLabelProps={{ shrink: true }}
    />
), (prev, next) => (
    prev.controlName === next.controlName &&
    prev.isReadOnly === next.isReadOnly &&
    prev.error === next.error &&
    prev.field.value === next.field.value
))

const DynamicNumberField = React.memo(({
                                           controlName,
                                           controlData,
                                           isReadOnly,
                                           error,
                                           field,
                                           onBlur,
                                       }: {
    controlName: string
    controlData: FieldListObject
    isReadOnly: boolean
    error: any
    field: any
    onBlur: (controlName: string, e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}) => (
    <NumberFormat
        {...field}
        customInput={TextField}
        label={controlData.lbl}
        name={controlName}
        size={'small' as any}
        allowNegative={false}
        decimalScale={2}
        fixedDecimalScale={false}
        allowLeadingZeros={false}
        variant="outlined"
        fullWidth
        defaultValue=""
        required={controlData.req}
        disabled={controlData.ro || isReadOnly}
        onBlur={(e) => onBlur(controlName, e)}
        error={!!error}
        helperText={error ? error.message : null}
        InputLabelProps={{ shrink: true }}
    />
), (prev, next) => (
    prev.controlName === next.controlName &&
    prev.isReadOnly === next.isReadOnly &&
    prev.error === next.error &&
    prev.field.value === next.field.value
))

const DynamicTimeField = React.memo(({
                                         controlName,
                                         controlData,
                                         isReadOnly,
                                         error,
                                         field,
                                         onBlur,
                                     }: {
    controlName: string
    controlData: FieldListObject
    isReadOnly: boolean
    error: any
    field: any
    onBlur: (controlName: string, e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}) => (
    <TextField
        {...field}
        label={controlData.lbl}
        name={controlName}
        fullWidth
        type="time"
        defaultValue=""
        size="small"
        required={controlData.req}
        disabled={controlData.ro || isReadOnly}
        onBlur={(e) => onBlur(controlName, e)}
        error={!!error}
        helperText={error ? error.message : null}
        InputLabelProps={{ shrink: true }}
    />
), (prev, next) => (
    prev.controlName === next.controlName &&
    prev.isReadOnly === next.isReadOnly &&
    prev.error === next.error &&
    prev.field.value === next.field.value
))

const getOrdinal = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd']
    const v = n % 100
    return n + (s[(v - 20) % 10] || s[v] || s[0])
}
interface DynamicGroupControlsProps {
    section: any
    sectionid: number
    dlist: DataListObject[]
    multiple: boolean
    isReadOnly: boolean
    errors: any
    control: any
    radioValues: { controlName: string; value: string }[]
    groupPhotos: any[]
    signatureLocked: { controlName: string; value: boolean }[]
    workReportingLookupData: any[]
    genericOnBlur: (controlName: string, e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    genericOnBlurText: (controlName: string, e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    genericTimeOnBlur: (controlName: string, e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    genericDateTimeOnBlur: (controlName: string, dateTime: any) => void
    handleRadioChange: (event: React.ChangeEvent<HTMLInputElement>, controlName: string) => void
    getRadioValue: (controlName: string) => string
    handleDynamicSetAdd: (sectionid: number, controlData: FieldListObject[], dlist: DataListObject[]) => void
    handleDynamicSectionDelete: (sectionid: number, controlData: FieldListObject[], dlistid2: string, dlist: DataListObject[]) => void
    getDynamicGroupPhotosSection: (sectionid: number, dlistid2: any, fieldid: any, controlData: any, dlistItem: any) => JSX.Element
    getMap: (mapValue: string) => JSX.Element
    getOptionsForGroupSelectNew: (keyValue: any, sectionid: any, schedulefieldheaderid: any) => JSX.Element[]
    getOptions: (dynamiclookuplist: any, optionTOLookup: any) => any[]
    getNewSignatureRef: (controlName: string) => any
    getSignatureRef: (controlName: string) => any
    setSignatureImageData: (controlName: string, controlData: any, dlistItem: any, base64Data: string) => void
    lockUnlockSignaturePad: (controlName: string, value: boolean) => void
    clearSignaturePad: (controlName: string) => void
    dataURLtoFile: (dataurl: string, filename: string) => File
}

const DynamicGroupControls = React.memo(({
                                             section,
                                             sectionid,
                                             dlist,
                                             multiple,
                                             isReadOnly,
                                             errors,
                                             control,
                                             radioValues,
                                             groupPhotos,
                                             signatureLocked,
                                             workReportingLookupData,
                                             genericOnBlur,
                                             genericOnBlurText,
                                             genericTimeOnBlur,
                                             genericDateTimeOnBlur,
                                             handleRadioChange,
                                             getRadioValue,
                                             handleDynamicSetAdd,
                                             handleDynamicSectionDelete,
                                             getDynamicGroupPhotosSection,
                                             getMap,
                                             getOptionsForGroupSelectNew,
                                             getOptions,
                                             getNewSignatureRef,
                                             getSignatureRef,
                                             setSignatureImageData,
                                             lockUnlockSignaturePad,
                                             clearSignaturePad,
                                             dataURLtoFile,
                                         }: DynamicGroupControlsProps) => {
    if (!section?.flist || section?.flist?.length === 0) return null

    const dlistDivider = (
        <Grid item xs={12} style={{ marginTop: '32px', marginBottom: '0px' }}>
            <Box style={{ height: '1px', width: '100%', backgroundColor: '#e0e0e0' }} />
        </Grid>
    )

    const multipleControl = (
        <>
            {(multiple && isReadOnly != true) &&
                <Box style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '24px', marginBottom: '8px' }}>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleDynamicSetAdd(sectionid, section?.flist, dlist)}
                        size="small"
                        color="primary"
                        style={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '6px',
                            padding: '6px 18px',
                            fontSize: '13px',
                        }}
                    >
                        Add a {getOrdinal((dlist?.length ?? 0) + 1)} Section
                    </Button>
                </Box>
            }
        </>
    )

    const multipleDlists = []
    const dlistLength = dlist?.length

    section.dlist?.map((dlistItem, index: number) => {
        const formControls = section?.flist
            .sort((a, b) => (a.grp > b.grp ? 1 : -1 && a.ord > b.ord ? 1 : -1))
            .map((controlData: FieldListObject, controlIndex) => {
                var control10Values = ''
                var control10Locked = false
                var control12Values = ''

                const controlName = sectionid.toString() + "--" + dlistItem.id2.toString() + "--" + controlData.id.toString() + "--" + controlData.ctrl.toString()

                if (controlData.ctrl == 10) {
                    const control10Obj = signatureLocked?.find(sigLockItem => sigLockItem.controlName === controlName)
                    if (control10Obj) {
                        control10Locked = control10Obj.value
                    }
                    const dataValuesObject = dlistItem.flist?.find(flistitem => (flistitem.id == controlData.id))
                    if (dataValuesObject && ('v' + controlData.ctrl) in dataValuesObject) {
                        control10Values = dataValuesObject['v' + controlData.ctrl]
                    } else {
                        const dataValuesObject2 = dlistItem.flist?.find(flistitem => (flistitem.id == controlData.id))
                        if (dataValuesObject2 && ('v' + controlData.ctrl) in dataValuesObject2) {
                            control12Values = dataValuesObject2['v' + controlData.ctrl]
                        }
                    }
                } else if (controlData.ctrl == 12) {
                    debugger
                    const dataValuesObject = dlistItem.flist?.find(flistitem => (flistitem.id == controlData.id))
                    if (dataValuesObject && ('v' + controlData.ctrl) in dataValuesObject) {
                        control12Values = dataValuesObject['v' + controlData.ctrl]
                    }
                }

                return (
                    <>
                        <FormControl style={{width: '100%'}}>
                            {(dlistLength > 1 && controlIndex == 0 && isReadOnly != true) &&
                                <>
                                    {multiple &&
                                        <Grid item xs={12} style={{ marginTop: '24px', marginBottom: '12px' }}>
                                            <Box style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                backgroundColor: '#f5f5f5',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '8px',
                                                padding: '8px 14px',
                                            }}>
                                                <Typography variant="subtitle2" style={{ color: '#555', fontWeight: 600, fontSize: '13px' }}>
                                                    {section?.sectiontitle ?? 'Section'} Section {index + 1}
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<DeleteIcon />}
                                                    disabled={isReadOnly}
                                                    onClick={() => handleDynamicSectionDelete(sectionid, section?.flist, dlistItem.id2, dlist)}
                                                    size="small"
                                                    style={{
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        borderRadius: '6px',
                                                        color: '#d32f2f',
                                                        borderColor: '#d32f2f',
                                                        padding: '4px 14px',
                                                        fontSize: '13px',
                                                    }}
                                                >
                                                    <Typography variant="subtitle2" style={{ color: '#555', fontWeight: 600, fontSize: '13px' }}>
                                                        Delete Section
                                                    </Typography>
                                                </Button>
                                            </Box>
                                        </Grid>
                                    }
                                </>
                            }

                            {controlData.ctrl === 1 &&
                                <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '15px'}}>
                                    <FormControl component="fieldset" fullWidth={true} disabled={controlData.ro || isReadOnly} required={controlData.req}>
                                        <FormLabel style={{color: controlData?.req && errors[controlName] ? 'red' : 'black'}} id={controlName}>{controlData.lbl}</FormLabel>
                                        <Controller
                                            name={controlName}
                                            control={control}
                                            rules={{ required: controlData.req ? 'Field is required' : false }}
                                            render={({ field, fieldState: { error } }) => (
                                                <RadioGroup
                                                    {...field}
                                                    value={getRadioValue(controlName)}
                                                    onChange={(e) => { handleRadioChange(e as React.ChangeEvent<HTMLInputElement>, controlName) }}
                                                    row
                                                    aria-labelledby={controlName}
                                                    name={controlName}
                                                >
                                                    <FormControlLabel value="1" control={<Radio required={controlData.req}/>} label="Yes" />
                                                    <FormControlLabel value="0" control={<Radio required={controlData.req}/>} label="No" />
                                                </RadioGroup>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                            }
                            {controlData.ctrl === 2 &&
                                <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '20px'}}>
                                    <FormControl required={true} fullWidth={true}>
                                        <Controller
                                            name={controlName}
                                            rules={{ required: controlData.req ? 'Field is required' : false }}
                                            defaultValue={null}
                                            render={({ field, fieldState: { error } }) => (
                                                <Autocomplete
                                                    {...field}
                                                    options={getOptions(workReportingLookupData, controlData.headerid)}
                                                    size={'small'}
                                                    fullWidth={true}
                                                    getOptionLabel={(option) => option?.label ? option?.label : ''}
                                                    onChange={(event, newValue) => field.onChange(newValue)}
                                                    value={field.value}
                                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                                    disabled={controlData.ro || isReadOnly}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            required={controlData.req}
                                                            name={controlName}
                                                            label={controlData.lbl}
                                                            error={!!error}
                                                            helperText={error ? error.message : null}
                                                        />
                                                    )}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                            }
                            {controlData.ctrl === 3 &&
                                <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '20px'}}>
                                    <Controller
                                        name={controlName}
                                        control={control}
                                        rules={{ required: controlData.req ? controlData.lbl + ' is required' : false }}
                                        render={({ field, fieldState: { error } }) => (
                                            <DynamicNumberFormatField
                                                controlName={controlName}
                                                controlData={controlData}
                                                isReadOnly={isReadOnly}
                                                error={error}
                                                field={field}
                                                onBlur={genericOnBlur}
                                            />
                                        )}
                                    />
                                </Grid>
                            }
                            {controlData.ctrl === 4 &&
                                <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '20px'}}>
                                    <Controller
                                        name={controlName}
                                        rules={{ required: controlData?.req ? 'Field is required' : false }}
                                        render={({ field, fieldState: { error } }) => (
                                            <DynamicNumberField
                                                controlName={controlName}
                                                controlData={controlData}
                                                isReadOnly={isReadOnly}
                                                error={error}
                                                field={field}
                                                onBlur={genericOnBlur}
                                            />
                                        )}
                                    />
                                </Grid>
                            }
                            {controlData.ctrl === 5 &&
                                <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '20px'}}>
                                    <Controller
                                        name={controlName}
                                        control={control}
                                        rules={{ required: controlData?.req ? 'Field is required' : false }}
                                        render={({ field, fieldState: { error } }) => (
                                            <DynamicTextField
                                                controlName={controlName}
                                                controlData={controlData}
                                                isReadOnly={isReadOnly}
                                                error={error}
                                                field={field}
                                                onBlur={genericOnBlurText}
                                            />
                                        )}
                                    />
                                </Grid>
                            }
                            {controlData.ctrl === 6 &&
                                <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '20px'}}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                            name={controlName}
                                            rules={{ required: controlData?.req ? 'Field is required' : false }}
                                            render={({ field, fieldState: { error } }) => (
                                                <DatePicker
                                                    key={controlName}
                                                    {...field}
                                                    label={controlData.lbl}
                                                    onAccept={(dateTime) => { genericDateTimeOnBlur(controlName, dateTime) }}
                                                    disableCloseOnSelect={false}
                                                    renderInput={(params) =>
                                                        <TextField
                                                            size={'small'}
                                                            {...field}
                                                            fullWidth={true}
                                                            required={controlData.req}
                                                            {...params}
                                                            onBlur={(e) => genericOnBlur(controlName, e)}
                                                            error={!!error}
                                                            helperText={error ? error.message : null}
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    }
                                                    disabled={controlData.ro || isReadOnly}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                            }
                            {controlData.ctrl === 7 &&
                                <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '20px'}}>
                                    <Controller
                                        name={controlName}
                                        control={control}
                                        rules={{ required: controlData?.req ? 'Field is required' : false }}
                                        defaultValue={[]}
                                        render={({ field, fieldState: { error } }) => (
                                            <FormControl fullWidth>
                                                <InputLabel shrink={true} style={{color: (error ? '#d32f2f' : 'rgba(0,0,0,0.5)')}} id="multiple-select-label">{controlData.lbl}</InputLabel>
                                                <Select
                                                    {...field}
                                                    notched
                                                    labelId="multiple-select-label"
                                                    id="multiple-select"
                                                    name={controlName}
                                                    multiple
                                                    size={'small'}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    label={controlData.lbl}
                                                    required={controlData.req}
                                                    disabled={controlData.ro || isReadOnly}
                                                    error={!!error}
                                                    renderValue={(selected: any) => (
                                                        <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                            {selected.map((val) => {
                                                                const match = workReportingLookupData.find(item => item.lookupid === Number(val))
                                                                return (
                                                                    <span
                                                                        key={val}
                                                                        style={{
                                                                            backgroundColor: '#e3f2fd',
                                                                            color: '#1565c0',
                                                                            borderRadius: '4px',
                                                                            padding: '2px 8px',
                                                                            fontSize: '12px',
                                                                            fontWeight: 500,
                                                                            lineHeight: '20px',
                                                                        }}
                                                                    >
                                                                        {match?.lookup ?? val}
                                                                    </span>
                                                                )
                                                            })}
                                                        </Box>
                                                    )}
                                                >
                                                    {getOptionsForGroupSelectNew(sectionid, controlData.id, controlData.headerid)}
                                                </Select>
                                                {!!error && (
                                                    <FormHelperText style={{color: '#d32f2f'}}>Field is required</FormHelperText>
                                                )}
                                            </FormControl>
                                        )}
                                    />
                                </Grid>
                            }
                            {controlData.ctrl === 8 &&
                                <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '20px'}}>
                                    <FormControl fullWidth={true}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <Controller
                                                name={controlName}
                                                rules={{ required: controlData?.req ? 'Field is required' : false }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <MobileDateTimePicker
                                                        key={controlName}
                                                        {...field}
                                                        label={controlData.lbl}
                                                        onAccept={(dateTime) => { genericDateTimeOnBlur(controlName, dateTime) }}
                                                        renderInput={(params) =>
                                                            <TextField
                                                                size={'small'}
                                                                {...field}
                                                                fullWidth={true}
                                                                required={controlData.req}
                                                                {...params}
                                                                error={!!error}
                                                                helperText={error ? error.message : null}
                                                                InputLabelProps={{ shrink: true }}
                                                            />
                                                        }
                                                        disabled={controlData.ro || isReadOnly}
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                </Grid>
                            }
                            {controlData.ctrl === 9 &&
                                <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '20px'}}>
                                    <Controller
                                        name={controlName}
                                        rules={{ required: controlData?.req ? 'Field is required' : false }}
                                        render={({ field, fieldState: { error } }) => (
                                            <DynamicTimeField
                                                controlName={controlName}
                                                controlData={controlData}
                                                isReadOnly={isReadOnly}
                                                error={error}
                                                field={field}
                                                onBlur={genericTimeOnBlur}
                                            />
                                        )}
                                    />
                                </Grid>
                            }
                            {controlData.ctrl === 10 &&
                                <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '20px'}}>
                                    <>
                                        <Typography variant="body2" gutterBottom>Signature</Typography>
                                        {(control10Values) &&
                                            <img height={'100px'} src={`data:image/png;base64,${control10Values}`}
                                                 alt="Signature" style={{paddingBottom: '20px', maxWidth: '100%'}}/>
                                        }
                                    </>
                                    {isReadOnly == false &&
                                        <>
                                            {!control10Locked &&
                                                <Controller
                                                    name={controlName}
                                                    control={control}
                                                    rules={{ required: controlData?.req ? 'Field is required' : false }}
                                                    render={({ field: { onChange, value } }) => (
                                                        <SignatureCanvas
                                                            ref={getNewSignatureRef(controlName)}
                                                            canvasProps={{className: (controlData?.req && errors[controlName] ? styles.sigPadRed : styles.sigPad)}}
                                                            clearOnResize={false}
                                                            onEnd={async () => {
                                                                const sigReference = getSignatureRef(controlName)
                                                                const signCanvasFile = dataURLtoFile(sigReference?.current?.getTrimmedCanvas().toDataURL('image/png'), controlName)
                                                                const fileReader = new FileReader()
                                                                fileReader.onloadend = function (event) {}
                                                                fileReader.onload = function (event) {
                                                                    const data = event.target.result as string
                                                                    const dataSpit = data.split(';base64,')[1]
                                                                    setSignatureImageData(controlName, controlData, dlistItem, dataSpit)
                                                                }
                                                                new Compressor(signCanvasFile, {
                                                                    quality: 0.4,
                                                                    success(result) {
                                                                        fileReader.readAsDataURL(result)
                                                                    },
                                                                    error(err) {
                                                                        console.log(err.message);
                                                                    },
                                                                });
                                                            }}
                                                            value={value}
                                                        />
                                                    )}
                                                />
                                            }
                                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                                <button type="button" name={'clear'} className={styles.buttons} onClick={() => { lockUnlockSignaturePad(controlName, !control10Locked) }}>
                                                    {control10Locked ? 'Unlock' : 'Lock'}
                                                </button>
                                                {!control10Locked &&
                                                    <button type="button" name={'clear'} className={styles.buttons} onClick={() => { clearSignaturePad(controlName) }}>
                                                        Clear
                                                    </button>
                                                }
                                            </Box>
                                        </>
                                    }
                                </Grid>
                            }
                            {controlData.ctrl === 11 &&
                                <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '20px'}}>
                                    {getDynamicGroupPhotosSection(sectionid, dlistItem.id2, controlData.id, controlData, dlistItem)}
                                </Grid>
                            }
                            {(controlData.ctrl === 12 && controlData.ctrlo == 1) &&
                                <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '20px'}}>
                                    {getMap(control12Values)}
                                </Grid>
                            }
                            {controlData.ctrl === 13 &&
                                <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '20px'}}>
                                    <div style={{width: '100%'}}>{controlData.lbl}</div>
                                </Grid>
                            }
                            {controlData.ctrl === 14 &&
                                <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '10px'}}>
                                    <Grid item xs={controlData.wd} className={styles.selectcontrol} style={{marginTop: '10px'}}>
                                        <div style={{width: '100%'}}>
                                            <div dangerouslySetInnerHTML={{__html: controlData.lbl}} />
                                        </div>
                                    </Grid>
                                </Grid>
                            }
                        </FormControl>
                    </>
                )
            })

        if (dlistLength > 1 && dlistLength > (index + 1)) {
            multipleDlists.push([formControls, dlistDivider])
        } else {
            multipleDlists.push(formControls)
        }
    })

    return <>{[multipleControl, ...multipleDlists, multipleControl]}</>

}, (prev, next) => {
    return (
        prev.dlist === next.dlist &&
        prev.isReadOnly === next.isReadOnly &&
        prev.radioValues === next.radioValues &&
        prev.groupPhotos === next.groupPhotos &&
        prev.signatureLocked === next.signatureLocked &&
        prev.errors === next.errors &&
        prev.workReportingLookupData === next.workReportingLookupData
    )
})

const WorkReportingPage = (params) => {
    // const userGuid = params.userGuid
    const { userGuid } = useParams<Params>()
    const formMethods = useForm({
        mode: 'onBlur',
        reValidateMode: "onChange",
        shouldUnregister: false
    })
    const {trigger, setValue, resetField, getValues, getFieldState, control, reset, register, formState: { isDirty, dirtyFields, submitCount, errors }} = formMethods
    const { watch } = formMethods
    const dispatch = useDispatch()
    const { apiUrl, workreportingappversion, mapapikey } = getConfig()
    const [disableSubmit, setDisableSubmit] = useState(false)
    const [offlinePhotoIds, setOfflinePhotoIds] = React.useState([])
    const [dialogObj, setDialogObj] = React.useState({ msg: '', id: 0 })
    const [dispatchDataHold, setDispatchDataHold] = React.useState<WorkReportingDataObject>(null)
    const [dialogObjPhoto, setDialogObjPhoto] = React.useState({ msg: '', name: '', id: 0 })
    const [dialogMsg, setDialogMsg] = React.useState("")
    const [photos, setPhotos] = React.useState([])
    const [groupPhotos, setGroupPhotos] = React.useState([])
    const [documents, setDocuments] = React.useState([])
    const [dispatchData, setDispatchData] = React.useState<WorkReportingDataObject>(null);
    const [viewDataBody, setViewDataBody] = React.useState({id: ''})
    const [watchLogoDataURL, setWatchLogoDataURL] = React.useState(null)
    const [documentData, setDocumentData] = React.useState({filedata: '', fileext: '', filename: '', fileurl: '', id: '', serverModified: null})
    const [openViewer, setOpenViewer] = React.useState(false)
    const [documentsLoading, setDocumentsLoading] = React.useState([])
    const [titleMessage, setTitleMessage] = React.useState('Loading Work Reporting')
    const [loadingBackdrop, setLoadingBackdrop] = React.useState(false)
    const [formLoaded, setFormLoaded] = React.useState(false)
    const [expandedState, setExpandedState] = React.useState([])
    const [saveDocument, setSaveDocument] = React.useState(0)
    const [errorSections, setErrorSections] = useState([])
    const [tempSaveId, setTempSaveId] = useState(0)
    const [tempFieldSaveId, setTempFieldSaveId] = useState(0)
    const [syncServer, setSyncServer] = React.useState<number>(1)
    const [syncServerMessageCount, setSyncServerMessageCount] = React.useState([])
    const [photoUploadMessageCount, setPhotoUploadMessageCount] = React.useState([])
    const [snackbarOpen, setSnackbarOpen] = React.useState({ open: false, message: '' })
    const [workReportingLookupData, setWorkReportingLookupData] = React.useState([])
    const networkOnline = useNetworkOnline()
    const [systemVersion, setSystemVersion] = React.useState()
    const [radioValues, setRadioValues] = React.useState<{controlName: string, value: string}[]>([])
    const [watchId, setWatchId] = useState(null);

    const lockedValues = JSON.parse(localStorage.getItem(userGuid))
    const [signatureLocked, setSignatureLocked] = React.useState<{controlName: string, value: boolean}[]>(lockedValues ? lockedValues : [])
    const [dateTimeValues, setDateTimeValues] = useState<{ sectionid: number, schedulefieldid: number, dateTimeValue: Dayjs }[]>([])
    const [preloadingData, setPreloadingData] = useState({woid: null, tempsaveid: null, tempfieldsaveid: null, scheduleid: null, dispatchsendid: null, title: null, isreadonly: false})
    const [gpsData, setgpsData] = useState(null);
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);


    var signatureRefs = []
    const labelRegex = /<(.*?)>/g



    useEffect(() => {
        if (!navigator.geolocation) {
            debugger
            return;
        }

        const successHandler = (position) => {
            debugger
            setLatitude(position.coords.latitude);
            setLongitude(position.coords.longitude);
            console.log(position.coords)
        };

        const errorHandler = (err) => {
            debugger
        };

        navigator.geolocation.getCurrentPosition(successHandler, errorHandler, {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0,
        });
    }, [gpsData])

    useEffect(() => {
        if (signatureLocked && signatureLocked?.length > 0) {
            localStorage.setItem(userGuid, JSON.stringify(signatureLocked));
        }
    }, [signatureLocked])

    const clearSignaturePad = (controlName) => {
        const foundRef = signatureRefs.find(ref => ref.key == controlName)
        if (foundRef) {
            foundRef.ref.current.clear()
            foundRef.base64Data = ""
        }
    }

    const lockUnlockSignaturePad = (controlName, value) => {
        setSignatureLocked(prevState => {
            const foundRef = prevState.filter(ref => ref.controlName != controlName)
            return [...foundRef, {controlName: controlName, value: value}]
        })
    }

    const getNewSignatureRef = (controlName) => {
        const currentSigRef = getSignatureRef(controlName)
        if (currentSigRef) {
            return currentSigRef
        }
        else {
            const sigRef = React.createRef() as React.MutableRefObject<any>
            const sigObj = {key: controlName, ref: sigRef, base64Data: null}
            signatureRefs.push(sigObj)
            return sigRef
        }
    }

    const getSignatureRef = (controlName) => {
        const foundRef = signatureRefs.find(ref => ref.key == controlName)
        if (foundRef) {
            return foundRef.ref
        }
        else {
            return null
        }
    }

    const setSignatureImageData = (controlName, controlData, dlistItem, base64Data) => {
        const foundRef = signatureRefs.find(ref => ref.key == controlName)
        if (foundRef) {
            foundRef.base64Data = base64Data
            var idsArray = controlName.split("--")
            setErrorSections(prevState => {
                return [...prevState?.filter(item => item != controlName)]
            })

            const dataValuesObject = dlistItem.flist?.find(flistitem => (flistitem.id == controlData.id))
            if (dataValuesObject && ('v' + controlData.ctrl) in dataValuesObject) {
                dataValuesObject['v' + controlData.ctrl] = base64Data
            }
            else {
                const controlNameVar = 'v' + controlData.ctrl
                var dataToAdd = {
                    "id": controlData.id,
                }
                dataToAdd[controlNameVar] = base64Data
                dlistItem.flist.push(dataToAdd)
            }

            resetField(controlName, { keepError: false, keepTouched: true, defaultValue: base64Data })
            processFormUpdates(idsArray[0], idsArray[1], idsArray[2], idsArray[3], base64Data)
        }
        else {
            return null
        }
    }

    const hasSignatureImageData = (controlName) => {
        const foundRef = signatureRefs.find(ref => ref.key == controlName)
        if (foundRef) {
            if (foundRef.base64Data?.length > 0) {
                return true
            }
            else {
                return false
            }
        }
        else {
            return false
        }
    }

    const allWatchedFormValues = watch();

    const versionCompare = (v1, v2) => {
        const parts1 = v1.split('.').map(Number);
        const parts2 = v2.split('.').map(Number);

        for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
            const p1 = parts1[i] || 0;
            const p2 = parts2[i] || 0;

            if (p1 > p2) return 1;
            if (p1 < p2) return -1;
        }

        return 0;
    };

    useEffect(() => {
        const token = localStorage.getItem('token')
        if (token) {
            const decoded: any = jwtDecode(token)
            setSystemVersion(decoded.systemversion)
        }

        setGroupPhotos([])
        getworkreportingpre()
    }, [])

    const deepCompare = (previousformValues, newformValues) => {
        const previousKeys = Object.keys(previousformValues)
        const newKeys = Object.keys(newformValues)
        var keysneedUpdated = []

        for (const newkey of newKeys) {
            if (previousKeys.includes(newkey)) {
                if (newformValues[newkey] != undefined) {
                    if (previousformValues[newkey] != newformValues[newkey]) {
                        keysneedUpdated.push({id: newkey, value: newformValues[newkey]})
                    }
                }
            }
            else {
                if (newformValues[newkey] != undefined) {
                    if (previousformValues[newkey] != newformValues[newkey]) {
                        keysneedUpdated.push({id: newkey, value: newformValues[newkey]})
                    }
                }
            }
        }
        return keysneedUpdated
    }

    const processFormUpdates = async (sectionid, dlistid, controlid, controltype, valueToUpdate) => {
        const controlName = 'v' + controltype
        var body = {'guid': userGuid, sectionid: sectionid, dlistid2: dlistid, fieldid: controlid }
        if (controltype == 2) {
            body[controlName] = valueToUpdate?.value ? valueToUpdate?.value : null
        }
        else {
            body[controlName] = valueToUpdate
        }

        axios.put(`workreportingsavefield`, body).then((response) => {
            dispatchData?.sections?.map((section) => {
                if (section.sectionid == sectionid) {
                    section?.dlist.map((dataList) => {
                        if (dataList.id2 == dlistid) {
                            dataList?.flist.map(fielddatalist => {
                                if (fielddatalist.id == controlid) {
                                    fielddatalist[controlName] = valueToUpdate
                                }
                            })
                        }
                    })
                }
            })

            const dataJson = JSON.parse(response.data.jsonString)
            setTempFieldSaveId(dataJson.tempfieldsaveid)
            setTempSaveId(dataJson.tempsaveid)
        }).catch(error => {
            dispatchData?.sections?.map((section) => {
                if (section.sectionid == sectionid) {
                    section?.dlist.map((dataList) => {
                        if (dataList.id2 == dlistid) {
                            dataList?.flist.map(fielddatalist => {
                                if (fielddatalist.id == controlid) {
                                    fielddatalist[controlName] = valueToUpdate
                                }
                            })
                        }
                    })
                }
            })
            writeDatabaseRecord(dispatchData, false)
        })
    }

    useEffect(()=> {
        if (submitCount != 0) {
            const errorKeys = Object.keys(errors)
            setErrorSections(prevState => {
                return [...errorKeys]
            })
        }
    }, [submitCount])

    const cleanseValues = (updatedKeys, formValues) => {
        var returnValues = []
        updatedKeys?.forEach(key => {
            var idsArray = key.split("--")
            var valid = false
            if (idsArray[3] == 1 && typeof formValues[key] === 'string') {
                valid = true
            }
            else if (idsArray[3] == 2 && typeof formValues[key] === 'object' && formValues[key]?.hasOwnProperty('value')) {
                valid = true
            }
            else if (idsArray[3] == 3 && typeof formValues[key] === 'string' && Number(formValues[key]) > 0)  {
                valid = true
            }
            else if (idsArray[3] == 4 && typeof formValues[key] === 'string' && Number(formValues[key]) > 0)  {
                valid = true
            }
            else if (idsArray[3] == 5 && typeof formValues[key] === 'string' && formValues[key]?.length > 0)  {
                valid = true
            }
            else if (idsArray[3] == 6 && typeof formValues[key] === 'string' && formValues[key]?.length > 0)  {
                valid = false
            }
            else if (idsArray[3] == 7 && formValues[key]?.length > 0) {
                valid = true
            }
            else if (idsArray[3] == 8 && typeof formValues[key] === 'string' && formValues[key]?.length > 0) {
                valid = true
            }
            else if (idsArray[3] == 9 && typeof formValues[key] === 'string' && formValues[key]?.length > 0) {
                valid = true
            }
            else if (idsArray[3] == 10 && typeof formValues[key] === 'string' && formValues[key]?.length > 0) {
                valid = true
            }

            if (valid) {
                returnValues[key] = formValues[key]
            }
        })
        return returnValues
    }

    useEffect(() => {
        console.log(allWatchedFormValues)
        const updatedKeys = Object.keys(allWatchedFormValues)
        const newFormValues = cleanseValues(updatedKeys, allWatchedFormValues)

        if (formLoaded && updatedKeys.length > 0) {
            const previousKeys = Object.keys(previousValues)
            const keysToUpdate = deepCompare(previousValues, newFormValues)

            if (keysToUpdate.length > 0) {
                console.log(keysToUpdate)
                keysToUpdate.map(items => {
                    var idsArray = items.id.split("--")
                    var itemValue = items.value
                    if (idsArray[3] == 1) {
                        const newItemValue = itemValue == '1'

                        previousValues[items.id] = itemValue
                        setErrorSections(prevState => {
                            return [...prevState?.filter(item => item != items.id)]
                        })
                        processFormUpdates(idsArray[0], idsArray[1], idsArray[2], idsArray[3], newItemValue)
                    }
                    else if (idsArray[3] == 2) {
                        previousValues[items.id] = itemValue
                        setErrorSections(prevState => {
                            return [...prevState?.filter(item => item != items.id)]
                        })
                        processFormUpdates(idsArray[0], idsArray[1], idsArray[2], idsArray[3], itemValue)
                    }
                    else if (idsArray[3] == 3) {
                        previousValues[items.id] = itemValue
                        setErrorSections(prevState => {
                            return [...prevState?.filter(item => item != items.id)]
                        })
                        if (errorSections?.length == 0) {
                            reset(previousValues)
                        }
                    }
                    else if (idsArray[3] == 4) {
                        previousValues[items.id] = itemValue
                        setErrorSections(prevState => {
                            return [...prevState?.filter(item => item != items.id)]
                        })
                        if (errorSections?.length == 0) {
                            reset(previousValues)
                        }
                    }
                    else if (idsArray[3] == 5) {
                        previousValues[items.id] = itemValue
                        setErrorSections(prevState => {
                            return [...prevState?.filter(item => item != items.id)]
                        })
                    }
                    else if (idsArray[3] == 7) {
                        const newItemValueTest = itemValue.join(',')
                        previousValues[items.id] = itemValue
                        processFormUpdates(idsArray[0], idsArray[1], idsArray[2], idsArray[3], newItemValueTest)
                    }
                    else if (idsArray[3] == 8) {
                        const dateObj = Date.parse(itemValue)

                        if (!isNaN(dateObj)) {
                            const newObj = dayjs(dateObj).format('YYYY-MM-DDTHH:mm:ss').toString()
                            previousValues[items.id] = newObj
                            setErrorSections(prevState => {
                                return [...prevState?.filter(item => item != items.id)]
                            })
                            if (errorSections?.length == 0) {
                                reset(previousValues)
                            }
                        }
                    }
                    else if (idsArray[3] == 10) {
                        previousValues[items.id] = itemValue
                        setErrorSections(prevState => {
                            return [...prevState?.filter(item => item != items.id)]
                        })
                        if (errorSections?.length == 0) {
                            reset(previousValues)
                        }
                    }
                    else {
                        previousValues[items.id] = items.value
                        setErrorSections(prevState => {
                            return [...prevState?.filter(item => item != items.id)]
                        })
                        processFormUpdates(idsArray[0], idsArray[1], idsArray[2], idsArray[3], itemValue)
                        if (errorSections?.length == 0) {
                            reset(previousValues)
                        }
                    }
                })
            }
        }
    }, [allWatchedFormValues])

    const getDBRecord = async (id: string) => {
        const data = await db.workreporting.get(id);
        return data;
    }

    const addDBRecord = async (id: string, jsonString: string) => {
        return db.workreporting.put({ json: jsonString, uuid: id }, id)
    }

    const loadDatabaseRecord = async (userGuidToPro) => {
        const record = await getDBRecord(userGuidToPro)
        if (record) {
            return JSON.parse(record.json)
        }
        else {
            return undefined
        }
    }

    const loadPhotosDatabaseRecord = async (userGuidToPro) => {
        const record = await getDBRecord(userGuidToPro + '-photos')
        if (record) {
            return JSON.parse(record.json)
        }
        else {
            return undefined
        }
    }

    const loadPhotosGroupDatabaseRecord = async (userGuidToPro) => {
        const record = await getDBRecord(userGuidToPro + '-groupphotos')
        if (record) {
            return JSON.parse(record.json)
        }
        else {
            return undefined
        }
    }

    const loadDocumentsDatabaseRecord = async (userGuidToPro) => {
        const record = await getDBRecord(userGuidToPro + '-documents')
        if (record) {
            return JSON.parse(record.json)
        }
        else {
            return undefined
        }
    }

    const getworkreportingpre = async () => {
        const lookupGuid = userGuid + '-pre'
        if (networkOnline) {
            axios.get('/workreportingpre/' + userGuid, {}).then((response) => {
                const parsedData = JSON.parse(response.data.jsonString)
                setPreloadingData(parsedData)
                addDBRecord(lookupGuid, response.data.jsonString).then(
                    id => {
                        console.log('ID Generated: ', id);
                        getworkreportinglookups()
                    },
                    error => {
                    }
                )
            }).catch(error => {
                getworkreportinglookups()
                getDBRecord(lookupGuid).then((record) => {
                    if (record && record?.json?.length > 0) {
                        setPreloadingData(JSON.parse(record?.json))
                    }
                })
            })
        }
        else {
            getworkreportinglookups()
            getDBRecord(lookupGuid).then((record) => {
                if (record && record?.json?.length > 0) {
                    setPreloadingData(JSON.parse(record?.json))
                }
            })
        }
    }


    const getworkreportinglookups = async () => {
        const lookupGuid = userGuid + '-lookups'
        if (networkOnline) {
            axios.get('/workreportinglookups/' + userGuid, {}).then((response) => {
                const parsedData = JSON.parse(response.data.jsonString)
                if (parsedData?.lookuplist && parsedData?.lookuplist?.length > 0) {
                    setWorkReportingLookupData(parsedData.lookuplist)
                }
                else {
                    getDispatchData()
                }

                const newData = JSON.stringify(parsedData.lookuplist)
                addDBRecord(lookupGuid, newData).then(
                    id => {
                        console.log('ID Generated: ', id);
                    },
                    error => {
                    }
                )
            }).catch(error => {
                getDBRecord(lookupGuid).then((record) => {
                    if (record && record?.json?.length > 0) {
                        setWorkReportingLookupData(JSON.parse(record?.json))
                    }
                })
            })
        }
        else {
            getDBRecord(lookupGuid).then((record) => {
                if (record && record?.json?.length > 0) {
                    setWorkReportingLookupData(JSON.parse(record?.json))
                }
            })
        }
    }

    function dataURLtoFile(dataurl: string, filename: string) {
        var arr = dataurl.split(","),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[arr.length - 1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }

    const getOptionsForGroupSelectNew = (keyValue, sectionid, schedulefieldheaderid) => {
        if (workReportingLookupData) {
            const lookupOptions = workReportingLookupData.filter((item) => (item.lookupheaderid === Number(schedulefieldheaderid) && item.lookupactive === true))
            if (lookupOptions && lookupOptions?.length > 0) {
                const arrayOfLookups = lookupOptions?.map(item => (
                    <MenuItem key={item.uniqueid} value={item.lookupid}>
                        {item.lookup}
                    </MenuItem>
                ))

                return arrayOfLookups
            }
            else {
                return [(<MenuItem key={1} value={1}>
                    {''}
                </MenuItem>)]
            }
        }
        else {
            return [(<MenuItem key={1} value={1}>
                {''}
            </MenuItem>)]
        }
    }

    const getGroupDynamicData = (sectionid) => {
        var dlistValuesData: DataListObject[] = []
        const dynamicData = dispatchData?.sections?.find((item) => item.sectionname === "*GROUPDYNAMIC*" && item.sectionid == sectionid)
        if (dynamicData && dynamicData?.dlist && dynamicData?.dlist.length > 0) {
            dynamicData?.dlist?.map(dlistdata => {
                var fieldListValuesData: FieldListValues[] = []
                if (dynamicData && dynamicData?.flist) {
                    dynamicData.flist.forEach(schedField => {
                        const formDataValue = getValues(sectionid.toString() + "--" + dlistdata.id2.toString() + "--" + schedField.id.toString() + "--" + schedField.ctrl.toString())

                        if (formDataValue?.toString()?.length > 0 || (schedField.ctrl === 4 || schedField.ctrl === 6 || schedField.ctrl === 7 || schedField.ctrl === 9)) {
                            var dataToSave = formDataValue

                            if ((schedField.ctrl == 3) && dataToSave.toString().length === 0) {
                                console.log('fail -  ' + dataToSave)
                            }
                            else {
                                if (schedField.ctrl == 1) {
                                    dataToSave = dataToSave == '1'
                                }
                                else if ((schedField.ctrl == 2)) {
                                    dataToSave = dataToSave.value
                                }
                                else  if (schedField.ctrl === 4) {
                                    dataToSave = Number(dataToSave.value)
                                }
                                else if (schedField.ctrl === 6) {
                                    const updatedSubmit = dateTimeValues.find(val => (val.schedulefieldid === schedField.id))
                                    if (updatedSubmit) {
                                        dataToSave = dayjs(updatedSubmit.dateTimeValue, "YYYY-MM-DD")
                                    }
                                }
                                else if (schedField.ctrl === 9) {
                                    const updatedSubmit = dateTimeValues.find(val => (val.schedulefieldid === schedField.id))

                                    if (updatedSubmit) {
                                        dataToSave = updatedSubmit.dateTimeValue
                                    }
                                }
                                else if(schedField.ctrl === 7) {
                                }
                                else if (schedField.ctrl === 12) {
                                    debugger
                                    if (schedField.ctrlo == 2) {
                                        dataToSave = latitude.toString() + ',' + longitude.toString()
                                    }
                                    else if (schedField.ctrlo == 3) {
                                        dataToSave = latitude.toString() + ',' + longitude.toString()
                                    }
                                }

                                const keySched = 'v' + schedField.ctrl
                                var newScheduleRec: FieldListValues = {
                                    'id': schedField.id,
                                }

                                newScheduleRec[keySched] = dataToSave
                                fieldListValuesData.push(newScheduleRec)
                            }
                        }
                    })
                }
                var id = dlistdata.id2
                if (dlistdata.id?.length > 0) {
                    id = dlistdata.id
                }
                else {
                    const unique_id = uuid()
                    const uuidTemp = unique_id.slice(0, 8)
                    id = uuidTemp
                }

                const dlistreturn = {
                    "id": id,
                    "id2": dlistdata.id2,
                    "ord": 1,
                    'flist': fieldListValuesData
                }
                dlistValuesData.push(dlistreturn)
            })
        }

        return dlistValuesData
    }

    const getSectionsData = (sectiondata, includePhotos: Boolean) => {
        if (sectiondata && sectiondata?.sections && sectiondata?.sections?.length > 0) {
            return sectiondata.sections.map((section) => {
                if (section.sectionname === '*GROUPDYNAMIC*') {
                    const dynamicFormData = getGroupDynamicData(section.sectionid)
                    return {
                        ...section,
                        "dlist": dynamicFormData,
                    }
                }
                else if (section.sectionname === "*DOCUMENTS*") {
                    return {
                        "sectionname": section.sectionname,
                        "sectiontitle": section?.sectiontitle,
                        "sectionid": section.sectionid,
                        "filelist": documents,
                        "sectiondata": section.sectiondata
                    }
                }
                else {
                    return section
                }
            })
        }
        else {
            return (
                <></>
            )
        }
    }

    const getSubmitData = (data, includePhotos: Boolean) => {
        let submitData = null;
        if (data) {

            const tempid = tempSaveId > 0 ? tempSaveId : data.tempsaveid
            const tempfieldid = tempFieldSaveId > 0 ? tempFieldSaveId : data.tempfieldsaveid
            submitData = {
                "dispatchsendid": data.dispatchsendid,
                "tempsaveid": tempid,
                "tempfieldsaveid": tempfieldid,
                "sectiondata": data.sectiondata,
                "isreadonly": data.isreadonly,
                "title": data.title,
                "sections": getSectionsData(data, includePhotos)
            }
        }

        return submitData
    }

    const sendSave = () => {
        const submitData = getSubmitData(dispatchData, true)
        console.log(submitData)
        return (submitData ? updateWorkReporting.mutateAsync(submitData) : null)
    }

    const getDispatchData = async () => {
        try {
            const { data } = await axios.get('/workreporting/' + userGuid, {})
            const parsedData = JSON.parse(data.jsonString)
            console.log(parsedData)
            if (parsedData?.status === 'failure') {
                setTitleMessage(parsedData?.message)
            }
            else if (parsedData && parsedData?.sections?.length) {
                var selection = parsedData?.sections?.find((item) => item.sectionname === '*DOCUMENTS*')

                if (selection?.filelist && selection?.filelist?.length > 0) {
                    const tempFileList = selection?.filelist?.map(fileItem => {
                        return {id: fileItem.Id, name: fileItem.Name, CreatedTime: fileItem.CreatedTime, ParentId: fileItem.ParentId, IsFolder: fileItem.IsFolder, SubItems: fileItem.SubItems}
                    })
                    selection.filelist = tempFileList
                }
                console.log(parsedData)
                setDispatchDataHold(parsedData as WorkReportingDataObject)
            }
        } catch (error) {
            console.log(Object.keys(error), error.message);
            setLoadingBackdrop(false)
            setDocumentsLoading([])

            const tempWorkreportingdata = {
                dispatchsendid: null,
                tempsaveid: null,
                tempfieldsaveid: null,
                title: null,
                scheduleid: null,
                sectiondata: null,
                isreadonly: false,
                sections: null,
                status: null,
                message: null,
                woid: null,
                dynamiclookuplist: null,
                dynamicfieldlist: null,
            }

            setDispatchDataHold(tempWorkreportingdata)
            setTitleMessage(error.message)
        }
    }

    useEffect(() => {
        if (workReportingLookupData && workReportingLookupData.length > 0) {
            getDispatchData()
        }
    }, [workReportingLookupData])

    useEffect(() => {
        if (networkOnline) {
            setSyncServer(syncServer+1)
        }
    }, [networkOnline])


    useEffect(() => {
        if (syncServer > 2) {
            writeDatabaseRecord(dispatchData, true)
            writeDatabaseRecordForGroupPhotos(true)
        }
    }, [syncServer])

    const updateWorkReporting = useUpdateWorkReporting()

    const saveDBRec = async (userGuidToPro) => {
        const record = await getDBRecord(userGuidToPro)
        if (record) {
            const parsedDataJSON = JSON.parse(record.json)
            const parsedData = {...parsedDataJSON}
            if (parsedData.sections && parsedData.sections.length > 0) {
                parsedData.sections.map((section) => {
                    if (section.sectionname === '*GROUPDYNAMIC*') {
                        if (section && section.dlist && section.dlist.length > 0) {
                            section.dlist.map((dlist) => {
                                if (dlist && dlist.flist && dlist.flist.length > 0) {
                                    dlist.flist.map((flist) => {
                                        if ("v2" in flist && typeof flist['v2'] === 'object') {
                                            flist['v2'] = flist['v2'].value
                                        }
                                    })
                                }
                            })
                        }
                    }
                })
            }

            axios.put(`workreportingsave`, parsedData).then((response) => {
                const dataJson = JSON.parse(response.data.jsonString)
                setTempSaveId(dataJson.tempsaveid)
            })
        }
    }

    const writeDatabaseRecord = (dataToSave, saveServer) => {
        if (dataToSave) {
            const newData = JSON.stringify(dataToSave)
            addDBRecord(userGuid, newData).then(
                id => {
                    console.log('ID Generated: ', id);
                    if (saveServer) {
                        saveDBRec(userGuid)
                    }
                },
                error => {
                    if (saveServer && networkOnline) {
                        saveDBRec(userGuid)
                    }
                    console.log(error);
                }
            )
        }
    }

    useEffect(() => {
        if (dispatchData) {
            writeDatabaseRecord(dispatchData, false)
        }
    }, [tempSaveId, tempFieldSaveId])

    useEffect(() => {
        console.log(tempSaveId)
    }, [tempSaveId])

    const getCreateSignatureObject = (controlName: string, base64Data?: string) => {
        const foundObject = signatureRefs.find(ref => ref.key == controlName)
        if (foundObject) {
            if (base64Data) {
                foundObject.base64Data = base64Data
            }
            return foundObject
        }
        else {
            const sigRef = React.createRef() as React.MutableRefObject<any>
            const sigObj = {key: controlName, ref: sigRef, base64Data: base64Data}
            signatureRefs.push(sigObj)
            return sigObj
        }
    }

    useEffect(() => {
        const internalReload = async () => {
            var databaseDBTempId = tempSaveId
            var databaseDBTempFieldSaveId = tempFieldSaveId

            if (dispatchDataHold?.isreadonly) {
                console.log('isReadOnly')
                db.workreporting.get(userGuid).then((record) => { db.workreporting.delete(userGuid) })
                db.workreporting.get(userGuid + '-pre').then((record) => { db.workreporting.delete(userGuid + '-pre') })
                db.workreporting.get(userGuid + '-lookups').then((record) => { db.workreporting.delete(userGuid + '-lookups') })
                db.workreporting.get(userGuid + '-photos').then((record) => { db.workreporting.delete(userGuid + '-photos') })
                db.workreporting.get(userGuid + '-groupphotos').then((record) => { db.workreporting.delete(userGuid + '-groupphotos') })
                db.workreporting.get(userGuid + '-documents').then((record) => { db.workreporting.delete(userGuid + '-documents') })

                setGroupPhotos([])
                setDispatchData(dispatchDataHold)
                if (dispatchDataHold && dispatchDataHold?.sections) {
                    dispatchDataHold?.sections?.map((dispatch, i) => {
                        if (dispatch && dispatch.sectionname === '*GROUPDYNAMIC*') {
                            if (dispatch && dispatch?.dlist && dispatch?.dlist?.length > 0) {
                                dispatch?.dlist?.map((dlistdata)=> {
                                    dlistdata?.flist?.map((dlistflist) => {
                                        const controlData = dispatch?.flist.find((findFlist) => findFlist.id == dlistflist.id)
                                        if (controlData.ctrl === 11) {
                                            if (dispatch.sectionid > 0) {
                                                const unique_id = uuid();
                                                const id = unique_id.slice(0, 8)
                                                setGroupPhotos(prevArray => [...prevArray, {
                                                    id: id,
                                                    photo: null,
                                                    filename: null,
                                                    size: 0,
                                                    fieldid: dlistflist.id,
                                                    dlistid2: dlistdata.id2,
                                                    guid: userGuid,
                                                    sectionid: dispatch.sectionid,
                                                    docid: null,
                                                    delete: false,
                                                    inprocess: false,
                                                }]);
                                                getTempGroupPhotos(dispatch.sectionid, dlistdata.id2, dlistflist.id, true, id)
                                            }
                                        }
                                    })
                                })
                            }
                        }
                    })
                }
            }
            else {
                db.workreporting.get(userGuid).then(
                    (record) => {
                        if (((!record && dispatchDataHold?.sectiondata?.length > 0) || (dispatchDataHold?.tempsaveid > databaseDBTempId && dispatchDataHold?.tempfieldsaveid > databaseDBTempFieldSaveId && dispatchDataHold?.sectiondata?.length > 0))) {
                            console.log('USING NEW RECORD!!')

                            if (dispatchDataHold && dispatchDataHold?.sections) {
                                console.log(dispatchData)
                                dispatchDataHold?.sections?.map((dispatch, i) => {
                                    if (dispatch && dispatch.sectionname === '*GROUPDYNAMIC*') {
                                        if (dispatch && dispatch?.dlist && dispatch?.dlist?.length > 0) {
                                            dispatch?.dlist?.map((dlistdata) => {
                                                dlistdata?.flist?.map((dlistflist) => {
                                                    const controlData = dispatch?.flist.find((findFlist) => findFlist.id == dlistflist.id)
                                                    const controlId = dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()
                                                    const controlName = 'v' + controlData.ctrl

                                                    if (controlData.ctrl == 4) {
                                                    }
                                                    else if (controlData.ctrl == 7) {
                                                    }
                                                    else if (controlData.ctrl == 10) {
                                                        const base64Data = dlistflist[controlName]
                                                        const objSig = getCreateSignatureObject(controlId, base64Data)

                                                        if (objSig.ref.current && base64Data) {
                                                            const preloadedSignature = "data:image/png;base64," + base64Data
                                                            objSig.ref.current.fromDataURL(preloadedSignature);
                                                        }
                                                    }
                                                    else if (controlData.ctrl === 11) {
                                                        if (dispatch.sectionid > 0) {
                                                            const unique_id = uuid();
                                                            const id = unique_id.slice(0, 8)
                                                            setGroupPhotos(prevArray => [...prevArray, {
                                                                id: id,
                                                                photo: null,
                                                                filename: null,
                                                                size: 0,
                                                                fieldid: dlistflist.id,
                                                                dlistid2: dlistdata.id2,
                                                                guid: userGuid,
                                                                docid: null,
                                                                delete: false,
                                                                inprocess: false,
                                                            }]);
                                                            getTempGroupPhotos(dispatch.sectionid, dlistdata.id2, dlistflist.id, true, id)
                                                        }
                                                    }
                                                    else if (controlData.ctrl === 12) {
                                                        if (controlData.ctrlo == 2) {
                                                            processFormUpdates(dispatch.sectionid, dlistdata.id2, controlData.id, controlData.ctrl.toString(), latitude.toString() + ',' + longitude.toString())
                                                        }
                                                    }
                                                })
                                            })
                                        }
                                    }
                                })
                            }
                            setTempSaveId(dispatchDataHold?.tempsaveid)
                            setTempFieldSaveId(dispatchDataHold?.tempfieldsaveid)
                            setDispatchData(dispatchDataHold)
                        }
                        else if (record) {
                            console.log('USING DATABASE RECORD!!')
                            console.log(dispatchDataHold)

                            loadDatabaseRecord(userGuid).then((data) => {
                                if (dispatchDataHold) {
                                    var dataSections = data?.sections?.find((section) => {
                                        return section?.sectionname === '*DOCUMENTS*'
                                    })
                                    const dataSectionsHold = dispatchDataHold?.sections?.find((section) => {
                                        return section?.sectionname === '*DOCUMENTS*'
                                    })
                                    if (dataSections?.filelist?.length !== dataSectionsHold?.filelist?.length) {
                                        dataSections.filelist = dataSectionsHold?.filelist
                                    }
                                }
                                if (data) {
                                    setDispatchData(data)
                                }
                            })

                            loadPhotosGroupDatabaseRecord(userGuid).then((photosData) => {
                                if (photosData && photosData.data) {
                                    console.log(photosData.data)
                                    setGroupPhotos(photosData.data)
                                }
                            })
                        }
                        else {
                            console.log('No data')
                            setDispatchData(dispatchDataHold)
                        }
                    },
                    (error) => {
                        console.log(error)
                        setDispatchData(dispatchDataHold)
                    },
                );
            }
        }
        if (dispatchDataHold) {
            internalReload()
        }
    }, [dispatchDataHold])

    useEffect(() => {
        setFormLoaded(true)
    }, [dispatchData])

    useEffect(() => {
        if (groupPhotos && groupPhotos?.length > 0) {
            const photosToUpdate = groupPhotos.filter(photo => photo.docid == null && photo.inprocess == false && photo.photo != null || photo.delete == true)
            if (photosToUpdate && photosToUpdate?.length > 0) {
                photosToUpdate.map(uploadData => {
                    if (uploadData.delete) {
                        const dataToUpdate = groupPhotos?.find(item => item.id == uploadData.id)
                        dataToUpdate['inprocess'] = true
                        const body = {
                            id: uploadData.id,
                            sectionid: uploadData.sectionid,
                            fieldid: uploadData.fieldid,
                            dlistid2: uploadData.dlistid2,
                            docid: uploadData.docid,
                            guid: userGuid,
                        }
                        axios.delete(`workreportingsavephoto`, {data: body}).then((response) => {
                            dataToUpdate['inprocess'] = false
                            setSyncServerMessageCount(prevState => {
                                const itemsFound = prevState.filter(item => item.id != uploadData.id)
                                return [...itemsFound]
                            })
                        }).catch(error => {
                            dataToUpdate['inprocess'] = false
                            setSyncServerMessageCount(prevState => {
                                const itemsFound = prevState.filter(item => item.id != uploadData.id)
                                return [...itemsFound, {id: uploadData.id}]
                            })
                            setSyncServer(prevState => {
                                return prevState+1
                            })
                        })
                    }
                    else {
                        uploadGroupPhoto(uploadData.filename, uploadData.id, uploadData.photo, uploadData.sectionid, uploadData.dlistid2, uploadData.fieldid)
                    }
                })
            }
            writeDatabaseRecordForGroupPhotos(false)
        }
    }, [groupPhotos, syncServer])

    useEffect(() => {
        getAppBarMessage()
        if (dispatchData && dispatchData?.sections && dispatchData?.sections.length > 0) {
            console.log(dispatchData)
            dispatchData?.sections?.map((dispatch, i) => {
                if (dispatch && dispatch.sectionname === '*GROUPDYNAMIC*') {
                    var dateObj = []
                    if (dispatch && dispatch?.dlist && dispatch?.dlist?.length > 0) {
                        dispatch?.dlist?.map((dlistdata) => {
                            dlistdata?.flist?.map((dlistflist) => {
                                const controlData = dispatch?.flist.find((findFlist) => findFlist.id == dlistflist.id)
                                const controlId = dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()
                                const controlName = 'v' + controlData.ctrl

                                const controlvalue = dlistflist[controlName]
                                if (controlvalue == undefined) {
                                    if (controlData.ctrl === 1) {
                                    }
                                    if (controlData.ctrl === 7) {
                                        setValue(dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString(), [])
                                        previousValues[dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()] = []
                                    }
                                    else if (controlData.ctrl == 2) {
                                    }
                                    else {
                                        if (controlData.ctrl == 2) {
                                        }
                                        else {
                                            setValue(dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString(), null)
                                            previousValues[dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()] = null
                                        }
                                    }
                                }
                                else {
                                    if (controlData.ctrl == 2) {
                                    }
                                    else {
                                        setValue(dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString(), controlvalue)
                                        previousValues[dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()] = controlvalue
                                    }
                                }
                                if (controlData.ctrl === 1) {
                                    if (controlvalue == undefined) {
                                    }
                                    else {
                                        const testControlName = dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()
                                        setRadioValues(prevValues => {
                                            return [...prevValues.filter(item => (item.controlName != testControlName)), {controlName: testControlName, value: controlvalue ? '1' : '0'}]
                                        })
                                        previousValues[dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()] = controlvalue ? '1' : '0'
                                    }
                                }
                                else if (controlData.ctrl === 2) {
                                    if (controlvalue == undefined) {
                                    }
                                    else {
                                        var lookupOption = null
                                        if (typeof controlvalue == 'object') {
                                            lookupOption = workReportingLookupData.find((item) => item.lookupid === Number(controlvalue.value))
                                        }
                                        else {
                                            lookupOption = workReportingLookupData.find((item) => item.lookupid === Number(controlvalue))
                                        }

                                        if (lookupOption) {
                                            const newOption = { value: lookupOption.lookupid, label: lookupOption.lookup }
                                            setValue(dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString(), newOption)
                                            previousValues[dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()] = newOption
                                        }
                                    }
                                }
                                else if (controlData.ctrl === 4) {
                                    if (controlvalue == undefined) {
                                        setValue(dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString(), '')
                                        previousValues[dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()] = ''
                                    }
                                    else {
                                        setValue(dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString(), controlvalue)
                                        previousValues[dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()] = controlvalue
                                    }
                                }
                                else if (controlData.ctrl === 5) {
                                    if (controlvalue == undefined) {
                                        setValue(dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString(), '')
                                        previousValues[dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()] = ''
                                    }
                                    else {
                                        setValue(dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString(), controlvalue)
                                        previousValues[dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()] = controlvalue
                                    }
                                }
                                else if (controlData.ctrl === 9) {
                                    dateObj.push({
                                        sectionid: dispatch.sectionid,
                                        schedulefieldid: controlData.id,
                                        dateTimeValue: controlvalue
                                    })
                                }
                                else if (controlData.ctrl === 6) {
                                    const dateField = controlvalue ? dayjs(controlvalue) : null
                                    dateObj.push({
                                        sectionid: dispatch.sectionid,
                                        schedulefieldid: controlData.id,
                                        dateTimeValue: dateField
                                    })
                                }
                                else if (controlData.ctrl === 7) {
                                    if (controlvalue == undefined || controlvalue?.length == 0) {
                                        setValue(dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString(), [])
                                        previousValues[dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()] = []
                                    }
                                    else {
                                        var values = controlvalue
                                        if (typeof controlvalue === 'string') {
                                            values = controlvalue.split(',')
                                        }

                                        setValue(dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString(), values)
                                        previousValues[dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()] = values
                                    }
                                }
                                else if (controlData.ctrl === 10) {
                                    const base64Data = controlvalue
                                    const objSig = getCreateSignatureObject(controlId, base64Data)

                                    if (objSig.ref.current && base64Data) {
                                        const preloadedSignature = "data:image/png;base64," + base64Data
                                        objSig.ref.current.fromDataURL(preloadedSignature);
                                        setValue(controlName, base64Data)
                                        previousValues[dispatch.sectionid.toString() + "--" + dlistdata.id2 + "--" + dlistflist.id.toString() + "--" + controlData.ctrl.toString()] = base64Data
                                    }
                                }
                            })
                        })
                    }

                    if (expandedState.find(es => es.sectionid == dispatch.sectionid) == undefined) {
                        const tempdefaultState = getExpandedDefault(dispatch.expand)
                        if (tempdefaultState || dispatch.expand == 3) {
                            setExpandedState([...expandedState, {sectionid: dispatch.sectionid, expanded: tempdefaultState}])
                        }
                    }
                }
            })
        }

        if (dispatchData && dispatchData.status !== 'failure') {
            setTempSaveId(dispatchData?.tempsaveid)
            setTempFieldSaveId(dispatchDataHold?.tempfieldsaveid)
        }
    }, [dispatchData])

    const getGroupPhotoSectionData = () => {
        return {
            "sectionname": "*GROUPPHOTOS*",
            "data": groupPhotos,
        }
    }

    const writeDatabaseRecordForGroupPhotos = (serverSave) => {
        const photoGuid = userGuid + '-groupphotos'
        const photoSectionData = JSON.stringify(getGroupPhotoSectionData())
        addDBRecord(photoGuid, photoSectionData).then(
            id => {
                if (serverSave && networkOnline) {
                    saveDBGroupPhotosRec(photoGuid)
                }
            },
            error => {
                if (serverSave && networkOnline) {
                    saveDBGroupPhotosRec(photoGuid)
                }
                console.log(error);
            }
        )
    }

    const saveDBGroupPhotosRec = async (userGuidToPro) => {
    }

    const getFileDownload = async (file: any) => {
        const body = {
            id: file.id,
            filename: file.filename,
            dispatchsendid: file.dispatchsendid
        }

        const documentObject = await getDocumentObj(body)
        return documentObject
    }

    const getDocumentsData = async (filelist: any) => {
        console.log(filelist)
        const fileDataArray = filelist.map( async (item) => {
            const body = {
                id: item.Id,
                filename: item.path,
                dispatchsendid: dispatchData.dispatchsendid
            }

            const fileData = await getFileDownload(body)

            return {
                "id": body.id,
                "filename": body.filename,
                "serverModified": item.serverModified,
                "filedata": fileData.filedata,
                "fileext": fileData.fileext
            }
        })

        console.log(await Promise.all(fileDataArray))
        return await Promise.all(fileDataArray)
    }

    const getMainLabels = () => {
        if (
            dispatchData &&
            dispatchData.sectiondata &&
            dispatchData.sectiondata.length > 0
        ) {
            return (dispatchData?.sectiondata).map((dispatch, i) => {
                return (
                    <>
                        <Grid item key={dispatch.value} border={0} xs={8}>
                            <Typography variant="body2" gutterBottom>
                                {dispatch.label}
                            </Typography>
                        </Grid>
                        <Grid item border={0} xs={4}>
                            <Typography variant="body2" gutterBottom>
                                {dispatch.value}
                            </Typography>
                        </Grid>
                    </>
                )
            })
        }
    }

    const handleExpandChange =
        (sectionid: number, defaultState: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
            const isFoundObj = expandedState.find(expItem => expItem.sectionid == sectionid)
            if (isFoundObj) {
                if (defaultState == 3) {
                    isFoundObj.expanded = true
                }
                else {
                    setExpandedState(expandedState.filter(expItem => expItem.sectionid != sectionid))
                }
            }
            else {
                const tempExpandedState = [...expandedState]
                setExpandedState([...tempExpandedState, {sectionid: sectionid, expanded: true}])
            }
        }

    const getExpandedDefault = (expandEnum) => {
        if (expandEnum === 1) {
            return false
        }
        else if (expandEnum === 2) {
            return true
        }
        else if (expandEnum === 3) {
            return true
        }
        else {
            return false
        }
    }

    const doesSectionHaveErrors = (sectionid) => {
        if (errorSections && errorSections?.length > 0) {
            const objFound = errorSections?.find(id => id.startsWith(sectionid))
            if (objFound) {
                return true
            }
        }
        return false
    }

    const genericOnBlur = useCallback((controlName: string, e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const idsArray = controlName.split("--")
        let itemValue: any = e?.target?.value.replace(/,/g, "")

        if (idsArray[3] === "6") {
            itemValue = dayjs(itemValue, "MM-DD-YYYY")
            setValue(controlName, itemValue)
        } else if (idsArray[3] === "8") {
            const dateObt = new Date(itemValue)
            itemValue = dayjs(dateObt)
            setValue(controlName, itemValue)
        }

        setErrorSections(prev => prev.filter(item => item !== controlName))
        resetField(controlName, { keepError: false, keepTouched: true, defaultValue: itemValue })
        processFormUpdates(idsArray[0], idsArray[1], idsArray[2], idsArray[3], itemValue)
    }, [processFormUpdates, setValue, resetField])

    const genericOnBlurText = useCallback((controlName: string, e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        var idsArray = controlName.split("--")
        processFormUpdates(idsArray[0], idsArray[1], idsArray[2], idsArray[3], e?.target?.value)
    }, [processFormUpdates])

    const genericTimeOnBlur = useCallback((controlName: string, dateTime: React.FocusEvent<HTMLInputElement>) => {
        const idsArray = controlName.split("--")
        setErrorSections(prev => prev.filter(item => item !== controlName))
        resetField(controlName, { keepError: false, keepTouched: true, defaultValue: dateTime.target.value })
        processFormUpdates(idsArray[0], idsArray[1], idsArray[2], idsArray[3], dateTime.target.value)
    }, [processFormUpdates, resetField])

    const genericDateTimeOnBlur = useCallback((controlName: string, dateTime: any) => {
        const idsArray = controlName.split("--")
        const dateObj = Date.parse(dateTime)
        if (!isNaN(dateObj)) {
            const newObj = dayjs(dateObj).format('YYYY-MM-DDTHH:mm:ss').toString()
            previousValues[controlName] = newObj
            setErrorSections(prev => prev.filter(item => item !== controlName))
            processFormUpdates(idsArray[0], idsArray[1], idsArray[2], idsArray[3], newObj)
            if (errorSections?.length === 0) reset(previousValues)
        }
    }, [processFormUpdates, reset, errorSections])

    const handleRadioChange = useCallback((event: React.ChangeEvent<HTMLInputElement>, controlName: string) => {
        const { value } = event.target
        setRadioValues(prev => [...prev.filter(item => item.controlName !== controlName), { controlName, value }])
        resetField(controlName, { keepError: false, keepTouched: true, defaultValue: value })
    }, [resetField])

    const getRadioValue = useCallback((controlName: string) => {
        return radioValues.find(item => item.controlName === controlName)?.value ?? ''
    }, [radioValues])

    const handleDynamicSetAdd = useCallback((sectionid: number, controlData: FieldListObject[], dlist: DataListObject[]) => {
        var body = {'guid': userGuid, sectionid: sectionid }
        debugger
        axios.put(`workreportingsavesection`, body).then((response) => {
            const dataJson = JSON.parse(response.data.jsonString)
            setTempFieldSaveId(dataJson.tempfieldsaveid)
            var newFlist = []
            if (dlist && dlist?.length > 0) {
                newFlist = dlist[0].flist.map(flisttemp => {
                    return {id: flisttemp.id}
                })
            }
            debugger
            dlist.push({
                'id': dataJson.dlistid2,
                'id2': dataJson.dlistid2,
                'ord': dlist?.length > 0 ? dlist.length + 1 : 1,
                'flist': newFlist,
            })
            setSyncServer(prev => prev + 1)
        })
    }, [userGuid])

    const handleDynamicSectionDelete = useCallback((sectionid: number, controlData: FieldListObject[], dlistid2: string, dlist: DataListObject[]) => {
        var body = {'guid': userGuid, sectionid: sectionid, dlistid2: dlistid2}
        axios.delete(`workreportingsavesection`, {data: body}).then((response) => {
            dispatchData?.sections.map((dispatch) => {
                dispatch.dlist = dispatch?.dlist?.filter(dList => dList.id2 != dlistid2)
            })
            setSyncServer(prev => prev + 1)
        })
    }, [userGuid, dispatchData])

    const getSectionsAccordion = () => {
        if (
            dispatchData &&
            dispatchData?.sections &&
            dispatchData?.sections.length > 0
        ) {
            return (dispatchData?.sections).map((section, i) => {
                if (section.sectionname === '*GROUPDYNAMIC*') {
                    return (
                        <Accordion disableGutters={true}
                                   onChange={handleExpandChange(section?.sectionid, section?.expand)}
                                   expanded={expandedState.find(expState => expState.sectionid == section?.sectionid) || section?.expand == 3}
                                   defaultExpanded={getExpandedDefault(section?.expand)}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1a-content"
                                id="panel1a-header"
                            >
                                <Typography sx={{ color: doesSectionHaveErrors(section?.sectionid) ? 'red' : 'black' }} variant="h6">{section?.sectiontitle ? section?.sectiontitle : 'Dynamic'}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box border={0}>
                                    <HookForm
                                        methods={formMethods}
                                        onSubmit={submitForm}
                                        className={styles.itemsForm}
                                        formProps={{ id: 'dynamic-group-form' }}>
                                        <Grid
                                            container
                                            border={0}
                                            direction="row"
                                            width={'100%'}
                                        >
                                            <DynamicGroupControls
                                                section={section}
                                                sectionid={section.sectionid}
                                                dlist={section.dlist}
                                                multiple={section.multiple}
                                                isReadOnly={dispatchData?.isreadonly}
                                                errors={errors}
                                                control={control}
                                                radioValues={radioValues}
                                                groupPhotos={groupPhotos}
                                                signatureLocked={signatureLocked}
                                                workReportingLookupData={workReportingLookupData}
                                                genericOnBlur={genericOnBlur}
                                                genericOnBlurText={genericOnBlurText}
                                                genericTimeOnBlur={genericTimeOnBlur}
                                                genericDateTimeOnBlur={genericDateTimeOnBlur}
                                                handleRadioChange={handleRadioChange}
                                                getRadioValue={getRadioValue}
                                                handleDynamicSetAdd={handleDynamicSetAdd}
                                                handleDynamicSectionDelete={handleDynamicSectionDelete}
                                                getDynamicGroupPhotosSection={getDynamicGroupPhotosSection}
                                                getMap={getMap}
                                                getOptionsForGroupSelectNew={getOptionsForGroupSelectNew}
                                                getOptions={getOptions}
                                                getNewSignatureRef={getNewSignatureRef}
                                                getSignatureRef={getSignatureRef}
                                                setSignatureImageData={setSignatureImageData}
                                                lockUnlockSignaturePad={lockUnlockSignaturePad}
                                                clearSignaturePad={clearSignaturePad}
                                                dataURLtoFile={dataURLtoFile}
                                            />
                                        </Grid>
                                    </HookForm>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )
                }
                else {
                    return (
                        <Accordion disableGutters={true}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1a-content"
                                id="panel1a-header"
                            >
                                <Typography variant={'h6'}>{section.sectionname}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box>
                                    <Grid
                                        container
                                        border={0}
                                        direction="row"
                                        alignItems="center"
                                        height={'100%'}
                                        width={'100%'}
                                    >
                                        {getAccordionDetails(
                                            section.sectionname,
                                            section.sectiondata
                                        )}
                                    </Grid>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )
                }
            })
        }
    }

    const handlePhotoDelete = (e, item) => {
        console.log(item)
        setDialogObjPhoto({
            msg: 'Are you sure you want to delete "' + item.name + '"?',
            name: item.name,
            id: item.id
        })
    }

    const handlePhotoDeleteConfirm = async () => {
        const finalData = [...photos]
        const itsIt = finalData.filter((item) => item.id !== dialogObjPhoto.id)
        console.log(itsIt)
        setPhotos(itsIt)
        if (!networkOnline) {
            var oPhotoIds = [...offlinePhotoIds]
            oPhotoIds.push(dialogObjPhoto.id)
            setOfflinePhotoIds(oPhotoIds)
        }
        else {
            deleteTempPhoto(dialogObjPhoto.id)
        }

        closeDialog()
        setSyncServer(syncServer+1)
    }

    const closeDialog = () => {
        setDialogObjPhoto({
            msg: '',
            name: '',
            id: 0
        })
        setDialogObj({
            msg: '',
            id: 0
        })
        setDialogMsg("")
    }

    const getTempGroupPhotos = (sectionid, dlistid2, fieldid, includeimagedata, id?) => {
        if (networkOnline) {
            const body = {
                "guid": userGuid,
                "sectionid": sectionid,
                "dlistid2": dlistid2,
                "fieldid": fieldid,
                "includeimagedata": includeimagedata,
            }

            axios.post(`workreportingsavephoto`, body).then((response) => {
                const dataJson = JSON.parse(response.data.jsonString)
                if (dataJson?.photos && dataJson?.photos?.length > 0) {
                    dataJson?.photos?.map(photoData => {
                        setGroupPhotos(prevArray => {
                            const newPrevArray = prevArray.filter(photoSearch => (photoSearch.id != id))
                            return [...newPrevArray, {
                                id: photoData.id,
                                photo: photoData.photo,
                                filename: photoData.filename,
                                size: 0,
                                fieldid: fieldid,
                                dlistid2: dlistid2,
                                guid: userGuid,
                                sectionid: sectionid,
                                docid: photoData.docid,
                                delete: false,
                                inprocess: false,
                            }]
                        });
                    })
                }
                else {
                    if (id) {
                        setGroupPhotos(prevArray => {
                            const photoFindData = prevArray.filter(photoSearch => (photoSearch.id != id))
                            return [...photoFindData]
                        });
                    }
                }
            })
        }
    }

    const deleteTempPhoto = (id) => {
        const body = {
            "dispatchsendid": dispatchData.dispatchsendid,
            "guid": userGuid,
            "photos":[{
                "id": id,
            }]
        }

        axios.delete(`workreportingtempphoto`, {data: body})
    }

    const uploadTempPhoto = (newPhotos: any) => {
        if (newPhotos && newPhotos.length > 0) {
            const body = {
                "dispatchsendid": dispatchData.dispatchsendid,
                "guid": userGuid,
                "photos":newPhotos.map(photo => {
                    return {
                        "id": photo.id,
                        "photo": photo.imageData,
                        "filename": photo.name,
                        "filetype": photo.type,
                        "filesize": photo.size
                    }
                })
            }
            axios.put(`workreportingtempphoto`, body)
        }
    }

    const getDocumentObj = async (body: any) => {
        try {
            const response = await axios.post(`gddispatchfiledownload`, body)
            const dataJson = JSON.parse(response.data.jsonString)
            console.log(dataJson.type)
            return dataJson
        }
        catch(err) {
            console.log(err)
        }
    }

    const handleDocumentListItemClick = async (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        item,
    ) => {
        event.stopPropagation()
        event.preventDefault()

        console.log(item)
        setDocumentData(item)
        setWatchLogoDataURL(item.fileurl);
        setOpenViewer(true)
    }

    const getDocumentAccordionDetails = (filelist) => {
        return (
            <>
                <Box sx={{ width: '100%', maxWidth: 300, bgcolor: 'background.paper' }}>
                    {documents && documents.length > 0 && documents.map((item) => (
                        <List component="nav" aria-label="secondary mailbox folder">
                            <ListItemButton
                                onClick={(event) => handleDocumentListItemClick(event, item)}
                            >
                                <ListItemText primary={item?.filename} />
                            </ListItemButton>
                        </List>
                    ))}
                    {documentsLoading && documentsLoading.length > 0 && documentsLoading.map((item) => (
                        <Grid
                            container
                            border={0}
                            direction="row"
                            alignItems="top"
                            height={'100%'}
                            width={'100%'}
                        >
                            <>
                                <List component="nav" aria-label="secondary mailbox folder">
                                    <Grid item border={0} xs={12}>
                                        <ListItemButton>
                                            <ListItemAvatar><CircularProgress/></ListItemAvatar><ListItemText primary={'Downloading ' + item.name} />
                                        </ListItemButton>
                                    </Grid>
                                </List>
                            </>
                        </Grid>
                    ))}
                </Box>
            </>
        )
    }

    const onSubmit = async (data: FormData) => {
        setSyncServer(syncServer+1)
    }

    const onSubmitDynamicGroup = async (data: FormData) => {
        setSyncServer(syncServer+1)
    }

    const deleteGroupPhoto = (sectionid, photoObject) => {
        const tempGroupPhoto = groupPhotos?.find(photo => (photo.id == photoObject.id))
        tempGroupPhoto['delete'] = true
        setGroupPhotos(prevState => {
            return [...groupPhotos]
        })

        const photoGuid = userGuid + '-groupphotos'
        const photoSectionData = JSON.stringify(
            {
                "sectionname": "*GROUPPHOTOS*",
                "data": groupPhotos,
            }
        )

        addDBRecord(photoGuid, photoSectionData).then(
            id => {},
            error => { console.log(error); }
        )
    }

    const uploadGroupPhoto = async (filename, id, photo, sectionid, dlistid2, fieldid) => {
        const dataToUpdate = groupPhotos?.find(item => item.id == id)
        dataToUpdate['inprocess'] = true

        const body = {
            id: id,
            photo: photo,
            filename: filename,
            sectionid: sectionid,
            fieldid: fieldid,
            dlistid2: dlistid2,
            guid: userGuid
        }
        axios.put(`workreportingsavephoto`, body).then((response) => {
            setSyncServerMessageCount(prevState => {
                const itemsFound = prevState.filter(item => item.id != id)
                return [...itemsFound]
            })
            setPhotoUploadMessageCount(prevState => {
                const itemsFound = prevState.filter(item => item.name != filename)
                return [...itemsFound]
            })
            const dataJson = JSON.parse(response?.data?.jsonString)
            const dataToUpdate = groupPhotos?.find(item => item.id == id)
            if (dataToUpdate) {
                dataToUpdate['docid'] = dataJson.docid
                dataToUpdate['inprocess'] = false
            }
        }).catch(error => {
            const dataToUpdate = groupPhotos?.find(item => item.id == id)
            dataToUpdate['inprocess'] = false
            writeDatabaseRecordForGroupPhotos(false)
            setPhotoUploadMessageCount([])
            setSyncServerMessageCount(prevState => {
                const itemsFound = prevState.filter(item => item.id != id)
                return [...itemsFound, {id: id}]
            })
        })
    }


    const startFileProcessing = async (sectionid, imgFile: any, controlData, dlistItem) => {
        try {
            if (!imgFile?.name) throw new Error('Please select a valid image file.');

            var watermarkLabel = controlData.lbl

            if (labelRegex.test(watermarkLabel)) {
                if (dlistItem?.flist && dlistItem?.flist?.length > 0) {
                    dlistItem?.flist?.forEach((value) => {
                        const keys = Object.keys(value);
                        if (keys && keys?.length > 1 && value[keys[1]]) {
                            watermarkLabel = watermarkLabel.replace('<' + value.id + '>', value[keys[1]])
                        }
                    })
                }
            }
            if (labelRegex.test(watermarkLabel)) {
                dispatchData?.sections?.map(section => {
                    section.dlist?.map((dlistItemTemp, index: number) => {
                        if (dlistItemTemp?.flist && dlistItemTemp?.flist?.length > 0 && dlistItemTemp.id2 == dlistItem.id2) {
                            dlistItemTemp?.flist?.forEach((value) => {
                                const keys = Object.keys(value);
                                if (keys && keys?.length > 1 && value[keys[1]]) {
                                    watermarkLabel = watermarkLabel.replace('<' + value.id + '>', value[keys[1]])
                                }
                            })
                        }
                    })
                })
            }

            watermarkLabel = watermarkLabel.replace('<DATETIME>', dayjs().format('MM/DD/YYYY hh:mmA'))

            if (dlistItem?.flist && dlistItem?.flist?.length > 0) {
                dlistItem?.flist?.forEach((value) => {
                    debugger
                    watermarkLabel = watermarkLabel.replace('<' + value.id + '>', 'N/A')
                })
            }

            const textWatermarkFile = await textWatermark(imgFile, watermarkLabel);

            return textWatermarkFile;
        } catch (error) {
            console.log(`${error}`);
        } finally {
        }
    };

    const textWatermark = async (imgOriginal: File, watermarkText: string, quality = 0.4) : Promise<Blob> => {
        return new Promise((resolve, reject) => {
            let canvas: any = document.createElement('canvas');
            canvas.imageSmoothingQuality = 'medium';

            let context: any = canvas.getContext('2d');

            let img = document.createElement('img');
            img.src = URL.createObjectURL(imgOriginal);

            img.onload = async () => {
                canvas.width = img.width;
                canvas.height = img.height;

                context.drawImage(img, 0, 0, canvas.width, canvas.height);

                const sidePadding = 40;
                const verticalPadding = 30;
                const fontSize = Math.max(Math.floor(canvas.width * 0.03), 28);
                const lineHeight = fontSize * 1.4;
                const maxTextWidth = canvas.width - sidePadding * 2;

                context.font = `bold ${fontSize}px Arial`;
                context.textAlign = "center";
                context.textBaseline = "bottom";

                const lines = wrapText(context, watermarkText, maxTextWidth);

                const bgHeight = lines.length * lineHeight + verticalPadding * 2;
                const bgY = canvas.height - bgHeight;

                context.fillStyle = 'rgba(14,13,13,0.65)';
                context.fillRect(0, bgY, canvas.width, bgHeight);

                context.fillStyle = 'rgba(255,255,255,0.75)';
                const startY = bgY + verticalPadding + fontSize;
                lines.forEach((line, i) => {
                    context.fillText(line, canvas.width / 2, startY + i * lineHeight);
                });

                return context.canvas.toBlob((blob: any) => resolve(blob), 'image/jpeg', quality);
            };

            img.onerror = () => reject(`${imgOriginal.name} is invalid image format.`);
        });
    };

    function wrapText(context: any, text: string, maxWidth: number): string[] {
        const words = text.split(' ');
        const lines: string[] = [];
        let currentLine = words[0];

        for (let i = 1; i < words.length; i++) {
            const word = words[i];
            const width = context.measureText(currentLine + " " + word).width;
            if (width < maxWidth) {
                currentLine += " " + word;
            } else {
                lines.push(currentLine);
                currentLine = word;
            }
        }
        lines.push(currentLine);
        return lines;
    }

    const onDropGroupPhoto = async (files: File[], sectionid, dlistid2, fieldid, controlData, dlistItem) => {
        const fileNames = files.map(tempFile => {
            return {'name': tempFile.name}
        })
        setPhotoUploadMessageCount(prevState => {
            return [...fileNames]
        })
        for (const fileToUpload of files) {

            const watermarkedImage = await startFileProcessing(sectionid, fileToUpload, controlData, dlistItem)

            const fileReader = new FileReader()

            fileReader.onloadend = async function (event) {
                const data = event.target.result as string
                const unique_id = uuid();
                const id = unique_id.slice(0, 8)
                const myImageData = data.split(';base64,')[1]

                setLoadingBackdrop(false)
                const newPhotos = {
                    id: id,
                    photo: myImageData,
                    filename: fileToUpload.name,
                    size: fileToUpload.size,
                    type: fileToUpload.type,
                    fieldid: fieldid,
                    dlistid2: dlistid2,
                    guid: userGuid,
                    sectionid: sectionid,
                    docid: null,
                    delete: false,
                    inprocess: false,
                }

                setGroupPhotos(prevState => [...prevState, newPhotos])
            }

            fileReader.readAsDataURL(watermarkedImage)
        }
    }


    const getDynamicGroupPhotosSection = (sectionid, dlistid2, fieldid, controlData, dlistItem) => {
        return (
            <>
                <ImageList
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        transform: 'translateZ(0)',
                        width: '100%'
                    }}
                    rowHeight={200}
                    gap={1}
                >
                    {dispatchData?.isreadonly != true &&
                        <ImageListItem key={'newImage'} style={{height:200, width:200, objectFit: 'none', border: '2px solid gray'}}>
                            <Dropzone onDrop={(files: File[]) => {onDropGroupPhoto(files, sectionid, dlistid2, fieldid, controlData, dlistItem)}}>
                                {({
                                      getRootProps,
                                      getInputProps,
                                      isDragActive,
                                      isDragAccept,
                                      isDragReject,
                                      acceptedFiles,
                                      fileRejections,
                                  }) => (
                                    <Paper style={{height:200, width:200}} {...getRootProps()} elevation={2}>Drag and Drop image(s) here, or click to select files
                                        <input {...getInputProps()} />
                                    </Paper>
                                )}
                            </Dropzone>
                        </ImageListItem>
                    }
                    {groupPhotos?.filter(photo=> photo.dlistid2 == dlistid2 && photo.fieldid == fieldid && photo.delete == false)?.map((item) => (
                        <ImageListItem key={item.id} sx={{height:200, width:200, objectFit: 'none', border: '2px solid gray'}}>
                            {item.photo == null &&
                                <Box
                                    className={styles.box}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    height={"100%"}
                                    width={"100%"}
                                >
                                    <CircularProgress />
                                </Box>
                            }

                            {item.photo != null &&
                                <>
                                    <ImageListItemBar
                                        key={item.id + '-delete'}
                                        sx={{
                                            background:
                                                'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, ' +
                                                'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)'
                                        }}
                                        title={item.name}
                                        position="top"
                                        actionIcon={
                                            <>
                                                {dispatchData?.isreadonly != true &&
                                                    <IconButton sx={{color: 'white'}} aria-label={`star ${item.name}`}>
                                                        <DeleteIcon onClick={(e) => {deleteGroupPhoto(sectionid, item)}}/>
                                                    </IconButton>
                                                }
                                            </>
                                        }
                                        actionPosition="left"
                                    />
                                    <img
                                        srcSet={`data:image/png;base64,${item.photo}`}
                                        src={`data:image/png;base64,${item.photo}`}
                                        alt={item.name}
                                        loading="eager"
                                        style={{maxHeight:200, maxWidth:200, objectFit: 'contain'}}
                                    />
                                </>
                            }
                        </ImageListItem>
                    ))}
                </ImageList>
            </>
        )
    }


    const getOptions = (dynamiclookuplist, optionTOLookup) => {
        if (dynamiclookuplist) {
            const lookupOptions = dynamiclookuplist.filter((item) => item.lookupheaderid === Number(optionTOLookup))

            return lookupOptions.map(options => (
                {label: options.lookup, value: options.lookupid}
            ))
        }
    }

    const getAccordionDetails = (name: string, sectionData: any) => {
        if (sectionData && sectionData.length > 0) {
            return sectionData.map((section) => {
                if (section.label === '*MAP*') {
                    return getMap(section.value)
                } else if (section.label && section.value) {
                    return (
                        <>
                            <Grid item border={0} xs={8}>
                                <Typography variant="body2" gutterBottom>
                                    {section.label}
                                </Typography>
                            </Grid>

                            <Grid item border={0} xs={4}>
                                <Typography variant="body2" gutterBottom>
                                    {section.value}
                                </Typography>
                            </Grid>
                        </>
                    )
                } else if (section.label) {
                    return (
                        <>
                            <Grid item border={0} xs={12}>
                                <Typography variant="body2" gutterBottom>
                                    {section.label}
                                </Typography>
                            </Grid>
                        </>
                    )
                } else if (section.value) {
                    return (
                        <>
                            <Grid item border={0} xs={12}>
                                <Typography variant="body2" gutterBottom>
                                    {section.value}
                                </Typography>
                            </Grid>
                        </>
                    )
                }
            })
        }
    }

    const getAppBarMessage = () => {
        if (dispatchData && dispatchData.status === 'failure') {
            setTitleMessage(dispatchData.message)
        }
        else if (dispatchData && dispatchData.title) {
            setTitleMessage(dispatchData.title)
        }
        else {
            setTitleMessage('Loading Work Reporting')
        }
    }

    const handleClose = (event: React.SyntheticEvent | Event, reason?: string) => {
        setSnackbarOpen({ open: false, message: ""  });
    }

    const submitForm = () => {
        if (!networkOnline) {
            setLoadingBackdrop(false)
            setSnackbarOpen( { open: true, message: "Data connection error - Work order is currently working in offline mode." })
        }
        else {
            setLoadingBackdrop(true)
            sendSave().then(
                (resp) => {
                    setLoadingBackdrop(false)
                    if (resp && resp.data) {
                        const parsedData = JSON.parse(resp.data.jsonString)

                        if (parsedData.status === 'success') {
                            setDisableSubmit(true)
                            getDispatchData()
                        }
                        setSnackbarOpen( { open: true, message: parsedData.message })
                    }
                    else {
                        setSnackbarOpen( { open: true, message: "Unexpected Error - Please try again." })
                    }
                }
            )
        }
    }

    const openMap = (lat, lng) => {
        window.open("https://maps.google.com?q="+ lat +"," + lng );
    }

    const getMimeType = (fileext) => {
        fileext = fileext.replace(/\./g, '');
        var contentType = 'image/png';
        if (fileext === 'png') {
            contentType = 'image/png'
        }
        else if (fileext === 'pdf') {
            contentType = 'application/pdf'
        }
        else if (fileext === 'jpg' || fileext === 'jpeg') {
            contentType = 'image/jpeg'
        }
        else if (fileext === 'docx') {
            contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        }
        else if (fileext === 'xlsx') {
            contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
        else if (fileext === 'xls') {
            contentType = 'application/vnd.ms-excel'
        }
        return contentType
    }

    const saveDocumentCallback = (documentItem) => {
        const mimeType = getMimeType(documentData.fileext)

        const formData = new FormData();
        formData.append('file', documentItem.blob, 'document.pdf');
        formData.append('GUID', userGuid);
        if (documentItem?.filename) {
            formData.append('filename', documentItem.filename);
        }
        if (documentItem?.woid > 0) {
            formData.append('woid', documentItem.woid);
        }
        formData.append('mimetype', mimeType);

        axios.put(`api/documentsbloboverwrite`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then((response) => {
            console.log(response)
            documentItem.setLoadingBackdrop(false)
        })
    }

    const getMap = (mapValue: string) => {
        var center = { lat: Number(latitude), lng: Number(longitude) }
        if (mapValue && mapValue?.length > 0) {
            center = mapValue.split(',').reduce(
                (acc: any, value, i) => {
                    if (i === 0) acc.lat = Number(value)
                    else acc.lng = Number(value)
                    return acc
                },
                { lat: 0, lng: 0 }
            )
        }

        return (center && center.lat > 0 ? (
            <Grid item height={'100%'} width={'100%'}>
                <Wrapper apiKey={mapapikey}>
                    <MapComponent
                        key={`${center.lat}-${center.lng}`}
                        center={center}
                        zoom={10}
                    />
                </Wrapper>
            </Grid>
        ) : <></>)
    }

    return (
        <>
            <AppBar position="absolute" className={cn(styles.appBar, styles.shift)}>
                <Typography style={{display: 'inline-block'}} variant="h6" color="primary" component="div" align="center">
                    {titleMessage}
                    <Typography textAlign="right" fontSize={'11px'} style={{display: 'inline-block', paddingLeft: '2px'}} variant="caption" color="textDisabled" component="div" >
                        {workreportingappversion}
                    </Typography>
                    {systemVersion && versionCompare(systemVersion, workreportingappversion) == 1 &&
                        <div style={{color: "red", textAlign: 'center', margin: '10px'}}> {'Current system version ' + workreportingappversion + ' latest version ' + systemVersion + ' Please update.'}</div>
                    }
                    {syncServerMessageCount && syncServerMessageCount?.length > 0 &&
                        <div style={{color: "red", textAlign: 'center', margin: '10px'}}> {(networkOnline ? syncServerMessageCount.length + ' change(s) are being processed' : 'Network Offline: ' + syncServerMessageCount.length + ' offline change(s) wil be processed when online.')}</div>
                    }
                    {photoUploadMessageCount && photoUploadMessageCount?.length > 0 &&
                        <div style={{color: "red", textAlign: 'center', margin: '10px'}}> {'Uploading ' + photoUploadMessageCount.length + ' photos...'}</div>
                    }
                </Typography>
            </AppBar>
            <>
                {watchLogoDataURL && (
                    <Grid item xs={6} className={styles.selectcontrol}>
                        <>
                            <Dialog
                                open={openViewer}
                                fullWidth={true}
                                maxWidth="xl"
                                aria-labelledby="alert-dialog-title"
                                aria-describedby="alert-dialog-description"
                            >
                                <DialogContent>
                                    <div className="PDF-viewer">
                                        <PdfViewerComponent
                                            save={true}
                                            document={watchLogoDataURL}
                                            item={documentData}
                                            woid={dispatchDataHold?.woid}
                                            saveDocumentCallback={saveDocumentCallback}
                                            saveDocument={saveDocument}
                                        />
                                    </div>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => {
                                        setOpenViewer(false)
                                        setDocumentData(null)
                                        setWatchLogoDataURL(null)
                                    }}>Cancel</Button>
                                    <Button onClick={() => {
                                        setOpenViewer(false)
                                        window.open('/dispatch/open/' + encodeURIComponent(viewDataBody.id), '_blank', 'noopener,noreferrer')
                                    }}>Open Window</Button>
                                </DialogActions>
                            </Dialog>
                        </>
                    </Grid>
                )}
            </>
            <>
                {snackbarOpen.open &&
                    <Snackbar
                        open={true}
                        autoHideDuration={4000}
                        onClose={handleClose}
                        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                    >
                        <Alert severity="success">
                            {snackbarOpen.message}
                        </Alert>
                    </Snackbar>
                }
            </>
            <>
                <Dialog
                    open={dialogObjPhoto.msg.length > 0}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            {dialogObjPhoto.msg}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialog}>Cancel</Button>
                        <Button onClick={handlePhotoDeleteConfirm}>DELETE</Button>
                    </DialogActions>
                </Dialog>
            </>

            <>
                <Dialog
                    open={dialogMsg.length > 0}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            {dialogMsg}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialog}>Ok</Button>
                    </DialogActions>
                </Dialog>
            </>

            <div className={styles.root}>
                <Container>
                    <Box>
                        <Grid
                            container
                            border={0}
                            direction="row"
                            alignItems="center"
                            height={'100%'}
                            width={'100%'}
                        >
                            {getMainLabels()}
                        </Grid>
                    </Box>
                    <Box>{getSectionsAccordion()}</Box>
                    <LoadingBackdrop open={loadingBackdrop} />
                    {syncServerMessageCount && syncServerMessageCount?.length > 0 &&
                        <div style={{color: "red", textAlign: 'center', margin: '10px'}}> {(networkOnline ? syncServerMessageCount.length + ' change(s) are being processed' : 'Network Offline: ' + syncServerMessageCount.length + ' offline change(s) wil be processed when online.')}</div>
                    }
                    {photoUploadMessageCount && photoUploadMessageCount?.length > 0 &&
                        <div style={{color: "red", textAlign: 'center', margin: '10px'}}> {'Uploading ' + photoUploadMessageCount.length + ' photos...'}</div>
                    }
                    {!disableSubmit && dispatchData && !dispatchData.isreadonly && dispatchData.sectiondata && dispatchData.sectiondata.length > 0 && syncServerMessageCount?.length == 0 && photoUploadMessageCount?.length == 0 &&
                        <>
                            <Button type="submit" form={'dynamic-group-form'} className={styles.buttons} style={{'paddingTop': '20px'}}>
                                Submit
                            </Button>
                        </>
                    }
                    {(!dispatchData || dispatchData === undefined ) &&
                        <div style={{display: 'flex', justifyContent: 'center'}}>
                            <CircularProgress />
                        </div>
                    }
                </Container>
            </div>
        </>
    )
}

export default WorkReportingPage